<script setup lang="ts">
import ArrowRight from '@assets/icons/arrow-right.svg';
import JrnysLogo from '@assets/icons/jrnys-logo-new.svg';
import CloseIcon from '@assets/icons/x.svg';
import { BaseDrawer } from '@components/base';
import {
  AUTH_ROUTES,
  isForgotPasswordPath,
  isLoginPath,
  isPasswordChangedPath,
  isResetLinkPath,
  isSetPasswordPath,
  normalizeAuthPath,
} from '@constants/auth-routes';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import AuthAccountActivate from './AuthAccountActivate.vue';
import AuthForgotPassword from './AuthForgotPassword.vue';
import AuthLogin from './AuthLogin.vue';
import AuthSetPassword from './AuthSetPassword.vue';
import AuthSignUp from './AuthSignUp.vue';

const AUTH_IMAGE_URL = 'https://uat.jrnys.com/jrnys/images/auth-bg.png';

const DRAWER_HOST_PAGES = new Set([
  'login',
  'forgot-password',
  'reset-link',
  'set-password',
  'password-changed',
]);

const props = withDefaults(
  defineProps<{
    page:
      | 'login'
      | 'sign-up'
      | 'forgot-password'
      | 'reset-link'
      | 'set-password'
      | 'password-changed'
      | 'account-activate';
    content?: Record<string, string>;
    initialOpenForgotPassword?: boolean;
    initialOpenResetPassword?: boolean;
  }>(),
  { content: () => ({}), initialOpenForgotPassword: false, initialOpenResetPassword: false },
);

const authDrawerRef = ref<InstanceType<typeof BaseDrawer> | null>(null);
const authDrawerMode = ref<'forgot' | 'reset'>('forgot');
const forgotDrawerSuccess = ref(false);
const resetDrawerSuccess = ref(false);
const toast = useToast();

/** Login shell: form visible with optional drawer (all auth URLs except sign-up / activate). */
const isLoginShell = () => DRAWER_HOST_PAGES.has(props.page);

const goHome = () => {
  window.location.href = '/';
};

const loginUrlWithSearch = () => `${AUTH_ROUTES.LOGIN}${window.location.search}`;

const openForgotPasswordDrawer = (shouldUpdateUrl = true, showSuccess = false) => {
  authDrawerMode.value = 'forgot';
  forgotDrawerSuccess.value = showSuccess;
  if (shouldUpdateUrl) {
    const url = showSuccess ? AUTH_ROUTES.RESET_LINK : AUTH_ROUTES.FORGOT_PASSWORD;
    window.history.pushState({}, '', `${url}${showSuccess ? '' : window.location.search}`);
  }
  authDrawerRef.value?.open();
};

const closeForgotPasswordDrawer = () => {
  window.history.replaceState({}, '', loginUrlWithSearch());
  authDrawerRef.value?.close();
  forgotDrawerSuccess.value = false;
};

const openResetPasswordDrawer = (shouldUpdateUrl = true, showSuccess = false) => {
  authDrawerMode.value = 'reset';
  resetDrawerSuccess.value = showSuccess;
  if (shouldUpdateUrl) {
    const url = showSuccess
      ? AUTH_ROUTES.PASSWORD_CHANGED
      : `${AUTH_ROUTES.SET_PASSWORD}${window.location.search}`;
    window.history.pushState({}, '', url);
  }
  authDrawerRef.value?.open();
};

const closeResetPasswordDrawer = () => {
  window.history.replaceState({}, '', loginUrlWithSearch());
  authDrawerRef.value?.close();
  resetDrawerSuccess.value = false;
};

const onForgotPasswordClick = () => {
  openForgotPasswordDrawer();
};

const onForgotSubmitted = () => {
  forgotDrawerSuccess.value = true;
  window.history.pushState({}, '', AUTH_ROUTES.RESET_LINK);
};

const onSetPasswordSubmitted = () => {
  authDrawerMode.value = 'reset';
  resetDrawerSuccess.value = true;
  window.history.pushState({}, '', AUTH_ROUTES.PASSWORD_CHANGED);
};

const resetDrawerTitle = computed(() => {
  const path = getNormalizedPath();

  if (isPasswordChangedPath(path) || resetDrawerSuccess.value) {
    return 'Forgot Password';
  }

  if (
    authDrawerMode.value === 'reset'
    || isSetPasswordPath(path)
    || (isLoginPath(path) && hasResetTokenParams())
  ) {
    return props.content?.resetTitle ?? 'Reset your password';
  }

  return 'Forgot Password';
});

const hasResetTokenParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return Boolean(searchParams.get('code') && searchParams.get('id'));
};

const getNormalizedPath = () => normalizeAuthPath(window.location.pathname);

const shouldOpenResetDrawer = () => {
  const path = getNormalizedPath();

  if (isPasswordChangedPath(path)) {
    return true;
  }

  if (
    isSetPasswordPath(path)
    || props.initialOpenResetPassword
  ) {
    return true;
  }

  return isLoginPath(path) && hasResetTokenParams();
};

const shouldOpenForgotDrawer = () => {
  const path = getNormalizedPath();

  if (isResetLinkPath(path)) {
    return true;
  }

  if (
    isForgotPasswordPath(path)
    || props.initialOpenForgotPassword
  ) {
    return true;
  }

  return false;
};

const openDrawerIfNeeded = () => {
  if (!isLoginShell()) {
    return;
  }

  if (shouldOpenResetDrawer()) {
    const path = getNormalizedPath();
    openResetPasswordDrawer(
      false,
      isPasswordChangedPath(path),
    );
    return;
  }

  if (shouldOpenForgotDrawer()) {
    const path = getNormalizedPath();
    openForgotPasswordDrawer(
      false,
      isResetLinkPath(path),
    );
  }
};

const onPopState = () => {
  if (!isLoginShell()) {
    return;
  }

  const path = getNormalizedPath();

  if (shouldOpenResetDrawer()) {
    openResetPasswordDrawer(
      false,
      isPasswordChangedPath(path),
    );
    return;
  }

  if (shouldOpenForgotDrawer()) {
    openForgotPasswordDrawer(false, isResetLinkPath(path));
    return;
  }

  if (isLoginPath(path)) {
    authDrawerRef.value?.close();
    forgotDrawerSuccess.value = false;
    resetDrawerSuccess.value = false;
  }
};

const onDrawerChange = (isOpen: boolean) => {
  if (isOpen || !isLoginShell()) {
    return;
  }

  if (isLoginPath(getNormalizedPath())) {
    return;
  }

  window.history.replaceState({}, '', loginUrlWithSearch());
  forgotDrawerSuccess.value = false;
  resetDrawerSuccess.value = false;
};

const showLinkExpiredToastIfNeeded = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const isLinkExpired = searchParams.get('linkExpired') === '1';
  const isActivated = searchParams.get('activated') === '1';

  if (!isLoginPath(window.location.pathname)) {
    return;
  }

  if (isActivated) {
    toast.success('Account activated successfully. Please sign in.');
    searchParams.delete('activated');
  }

  if (isLinkExpired) {
    toast.error('Link expired. Please request a new link.');
    searchParams.delete('linkExpired');
  }

  if (!isActivated && !isLinkExpired) {
    return;
  }

  const nextQuery = searchParams.toString();
  window.history.replaceState(
    {},
    '',
    `${AUTH_ROUTES.LOGIN}${nextQuery ? `?${nextQuery}` : ''}`,
  );
};

onMounted(async () => {
  await nextTick();
  showLinkExpiredToastIfNeeded();
  openDrawerIfNeeded();
  window.addEventListener('popstate', onPopState);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState);
});
</script>

<template>
  <div class="flex flex-col min-h-screen auth-page lg:flex-row lg:items-stretch">
    <div
      class="relative hidden w-full min-h-screen overflow-hidden auth-page__left bg-brown-500 lg:flex lg:w-1/2 lg:min-w-0 lg:shrink-0"
    >
      <img
        :src="AUTH_IMAGE_URL"
        alt=""
        class="absolute inset-0 size-full object-cover object-center lg:object-[center_40%]"
        width="1200"
        height="1600"
        decoding="async"
        fetchpriority="high"
      >
      <a
        href="/"
        class="relative z-10 inline-block p-20 text-white lg:p-48"
      >
        <JrnysLogo />
      </a>
    </div>

    <div class="relative flex flex-col flex-1 w-full min-w-0 min-h-screen p-20 auth-page__right bg-white-100 lg:w-1/2 lg:shrink-0 lg:p-48">
      <a
        href="/"
        class="absolute z-10 flex items-center justify-center left-20 top-28 size-36 text-deep-blue lg:hidden"
        aria-label="Back to home"
      >
        <ArrowRight class="rotate-180 size-24" />
      </a>

      <button
        type="button"
        class="absolute items-center justify-center hidden transition-colors right-40 top-40 text-deep-blue hover:opacity-80 lg:flex"
        aria-label="Close"
        @click="goHome"
      >
        <CloseIcon class="size-16" />
      </button>

      <div class="flex justify-center pt-32 lg:hidden">
        <a
          href="/"
          class="text-off-black-500"
        >
          <JrnysLogo class="w-full h-16" />
        </a>
      </div>

      <div class="mx-auto w-full max-w-[430px] flex-1 pt-32 lg:flex lg:items-center lg:pt-0">
        <AuthLogin
          v-if="isLoginShell()"
          :content="content"
          @open-forgot-password="onForgotPasswordClick"
        />
        <AuthSignUp
          v-else-if="page === 'sign-up'"
          :content="content"
        />
        <AuthAccountActivate v-else-if="page === 'account-activate'" />
      </div>

      <BaseDrawer
        v-if="isLoginShell()"
        ref="authDrawerRef"
        :title="resetDrawerTitle"
        :width="450"
        :close-on-overlay-click="false"
        position="right"
        :class-names="'!bg-white-100'"
        @change="onDrawerChange"
      >
        <button
          type="button"
          class="sr-only"
          aria-hidden="true"
        >
          trigger
        </button>
        <template #close-icon>
          <span class="flex items-center justify-center transition-colors size-36 text-deep-blue hover:opacity-80">
            <CloseIcon class="size-16" />
          </span>
        </template>
        <template #content>
          <AuthForgotPassword
            v-if="authDrawerMode === 'forgot'"
            :content="content"
            :in-drawer="true"
            :initial-success="forgotDrawerSuccess"
            @close="closeForgotPasswordDrawer"
            @submitted="onForgotSubmitted"
          />
          <AuthSetPassword
            v-else
            :content="content"
            :in-drawer="true"
            :initial-success="resetDrawerSuccess"
            @close="closeResetPasswordDrawer"
            @submitted="onSetPasswordSubmitted"
          />
        </template>
      </BaseDrawer>
    </div>
  </div>
</template>
